import {
    Button, ButtonProps
} from '@mui/material';
import {
    styled
} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
    colors
} from '../../styles/_colors';
import React from 'react';
import {
    useRouter
} from 'next/router';
import theme, {
    TypographyWeights
} from '../../figmaTheme';

interface SunnysideButtonProps extends ButtonProps {
    bgcolor?: any,
    hovercolor?: any,
    textcolor?: any,
    width?: string,
    height?: string,
    margin?: string,
    children: string | JSX.Element | JSX.Element[],
    url?: string,
    borderRadius?: string,
    padding?: string,
    external?: boolean,
    fontSize?: string,
    fontWeight?: number,
    passQueryToUrl?: boolean,
    buttonVariant?: ButtonVariants,
    buttonSize?: ButtonSizes
}

export enum ButtonVariants {
    PRIMARY = 'primary',
    OUTLINE = 'outline',
    TEXT = 'text'
}

export enum ButtonSizes {
    NONE = 'none',
    VERY_SMALL = 'verySmall',
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    HUGE = 'huge'
}

const baseStyles = makeStyles(() => ({
    base: {
        width: 'fit-content',
        '& p': {
            fontFamily: 'Poppins',
            fontWeight: TypographyWeights.MEDIUM,
        }
    }
}));

const variantStyles = makeStyles(() => ({
    [ButtonVariants.PRIMARY]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.white,
        textTransform: 'none',
        '& a > p, p': {
            color: 'inherit',
            textTransform: 'inherit'
        },
        '&:active, &:focus': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.white
        },
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.white
        }
    },
    [ButtonVariants.OUTLINE]: {
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        textTransform: 'none',
        '& a>p,p': {
            color: 'inherit',
            textTransform: 'inherit'
        },
        '&:active, &:focus': {
            border: `1px solid ${theme.palette.primary.main}`
        },
        '&:hover': {
            border: `1px solid ${theme.palette.primary.dark}`,
            color: theme.palette.primary.dark
        }
    },
    [ButtonVariants.TEXT]: {
        color: theme.palette.primary.main,
        textTransform: 'none',
        '& a': {
            color: 'inherit',
            textTransform: 'inherit'
        },
        '& a>p,p': {
            color: 'inherit',
            textTransform: 'inherit'
        },
        '&:active, &:focus': {
            color: theme.palette.primary.main,
        },
        '&:hover': {
            color: theme.palette.primary.dark,
        }
    }
}));

const sizeStyles = makeStyles(() => ({
    [ButtonSizes.NONE]: {
        padding: 0
    },
    [ButtonSizes.VERY_SMALL]: {
        padding: '4px 8px',
        borderRadius: '8px',
        '& p': { // TypographyVariants.SMALL
            fontSize: '12px',
            lineHeight: '16px'
        }
    },
    [ButtonSizes.SMALL]: {
        padding: '6px 8px',
        borderRadius: '10px',
        '& p': { // TypographyVariants.H6
            fontSize: '14px',
            lineHeight: '20px'
        }
    },
    [ButtonSizes.MEDIUM]: {
        padding: '10px 16px',
        borderRadius: '12px',
        '& p': { // TypographyVariants.H6
            fontSize: '14px',
            lineHeight: '20px'
        }
    },
    [ButtonSizes.LARGE]: {
        padding: '12px 16px',
        borderRadius: '16px',
        '& p': { // TypographyVariants.H5
            fontSize: '16px',
            lineHeight: '24px'
        }
    },
    [ButtonSizes.HUGE]: {
        padding: '16px',
        borderRadius: '16px',
        '& p': { // TypographyVariants.H4
            fontSize: '18px',
            lineHeight: '24px'
        }
    },
}));

function SunnysideButton(props: SunnysideButtonProps, ref: React.Ref<any>): JSX.Element {
    const baseClasses = baseStyles();
    const variantClasses = variantStyles();
    const sizeClasses = sizeStyles();

    const router = useRouter();
    const {
        bgcolor,
        hovercolor,
        textcolor,
        width,
        height,
        margin,
        children,
        url,
        borderRadius,
        padding,
        fontSize,
        fullWidth,
        fontWeight,
        passQueryToUrl,
        buttonVariant,
        buttonSize = ButtonSizes.HUGE,
        className,
        style,
        onClick
    } = props;
    const StyledButton = styled(Button, {
        shouldForwardProp: (prop) =>
            prop !== 'borderRadius'
            && prop !== 'buttonVariant'
            && prop !== 'buttonSize'
    })<ButtonProps>(({
        theme
    }) => ({
        height: height || '60px',
        margin: margin || '0px',
        backgroundColor: bgcolor || colors.sky,
        color: `${textcolor || colors.cloud} !important`,
        boxShadow: 'none',
        borderRadius: borderRadius || '0',
        padding: padding,
        fontWeight: fontWeight || 600,
        fontSize: fontSize ? `${fontSize} !important` : 'initial',
        '&:hover': {
            backgroundColor: hovercolor || colors.sky_hover,
            boxShadow: 'none'
        },
        [theme.breakpoints.down(400)]: {
            width: fullWidth ? null : width || '310px',
        },
        [theme.breakpoints.up(400)]: {
            width: fullWidth ? null : width || '360px',
        },
    }));

    /* Probably a better way to ensure formatting is corrected but quick implementation */
    const getUrl = (): string => {
        if (!passQueryToUrl) return url;
        const query = router.query;
        if (!query) return url;
        /* Add query to url when passQueryToUrl is true */
        const urlContainsQuery = url.includes('?');
        return url + Object.keys(query).reduce((p, k, i) => {
            const value = query[k];
            const formattedValue = Array.isArray(value) ? JSON.stringify(value) : value;
            return `${p}${i === 0 && !urlContainsQuery ? '?' : '&'}${k}=${formattedValue}`;
        }, '');
    };

    if (buttonVariant) {
        const dataProps = Object.keys(props)?.filter(key => key.startsWith('data-'));
        const dataPropsObject = dataProps?.reduce((acc, key) => {
            acc[key] = props[key];
            return acc;
        }, {});

        return (
            <Button
                href={url ? getUrl() : null}
                ref={ref}
                className={`${baseClasses.base} ${variantClasses[buttonVariant]} ${sizeClasses[buttonSize]} ${className || ''}`}
                style={style}
                onClick={onClick || null}
                {...dataPropsObject}
            >
                {children}
            </Button>
        );
    }

    return (
        url ?
            <StyledButton
                variant="contained"
                href={getUrl()}
                ref={ref}
                {...props}
            >
                {children}
            </StyledButton>
            :
            <StyledButton
                ref={ref}
                variant="contained"
                {...props}
            >
                {children}
            </StyledButton>
    );
}
export default React.forwardRef(SunnysideButton);
